import React, { useEffect, useState, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { apiService } from "lib/apiService";

const COLORS = [
  "#3b82f6", "#00b4d8", "#22c55e", "#60d394", "#f59e0b", "#f4a261"
];

const HighLevelPieChart = ({ years = [], selectedGlobalMonth, selectedGlobalYear, currency = "" }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(selectedGlobalMonth || new Date().getMonth() + 1);
  const [categoryData, setCategoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const storedUserData = localStorage.getItem("userData");
  let groupId = "";

  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    groupId = userData.groupId;
  }

  useEffect(() => {
    setSelectedMonth(selectedGlobalMonth);
  }, [selectedGlobalMonth]);

  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

  // Dark mode detection
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    setIsDarkMode(document.documentElement.classList.contains("dark"));
    return () => observer.disconnect();
  }, []);

  // Load Highcharts modules
  useEffect(() => {
    const initializeHighcharts = async () => {
      const HighchartsMore = (await import("highcharts/highcharts-more")).default;
      const Exporting = (await import("highcharts/modules/exporting")).default;
      HighchartsMore(Highcharts);
      Exporting(Highcharts);
    };
    initializeHighcharts();
  }, []);

  // Fetch data
  useEffect(() => {
    if (!groupId) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await apiService.getExpenseByMonth(groupId, selectedYear, selectedMonth);
        setCategoryData(data);
      } catch (err) {
        console.error("Error fetching category data:", err);
        setCategoryData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedYear, selectedMonth, groupId]);

  const chartData = useMemo(() => {
    return categoryData.map((cat, index) => ({
      name: cat.expenseDescType,
      y: cat.totalExpenses,
      color: COLORS[index % COLORS.length],
    }));
  }, [categoryData]);

  const options = useMemo(() => ({
    accessibility: { enabled: false },
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      animation: true,
      spacingLeft: 0,   // force right shift
      spacingRight: 120,
    },
    title: { text: undefined, style: { color: isDarkMode ? "#f3f4f6" : "#111827" } },
    tooltip: {
      formatter: function () {
        return `<b>${this.point.name}</b>: ${currency}${this.y.toLocaleString('en-IN', { maximumFractionDigits: 0 })} (${this.percentage.toFixed(1)}%)`;
      },
      backgroundColor: isDarkMode ? "#374151" : "#ffffff",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        borderRadius: 5,
        cursor: "pointer",
        depth: 45,
        innerSize: "50%",
        showInLegend: true,
        borderWidth: 3,
        borderColor: isDarkMode ? "#1f2937" : "#ffffff",
        dataLabels: {
          enabled: true,
          format: "{point.name}: {point.percentage:.1f}%",
          style: { fontSize: "11px" },
        },
        animation: { duration: 2500, easing: "linear" },
      },
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        color: isDarkMode ? "#fff" : "#444",
        fontWeight: "500",
        fontSize: "14px",
      },
    },
    series: [
      {
        type: "pie",
        name: "Expenses",
        data: chartData,
      },
    ],
    credits: { enabled: false },
  }), [chartData, isDarkMode, currency]);

  const monthOptions = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg mt-4" style={{ padding: '20px', height: "480px" }}>
      <div className="flex flex-col lg:flex-row justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 lg:mb-0">
          Expense Distribution
        </h2>

        <div className="flex gap-2 flex-col lg:flex-row">
          <select
            className="text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
            style={{ minWidth: "100px", border: "solid #ddd 1px" }}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {monthOptions.map((month, idx) => (
              <option key={month} value={idx + 1}>{month}</option>
            ))}
          </select>

          <select
            className="text-xs text-gray-800 dark:text-white rounded-md px-3 py-2"
            style={{ minWidth: "80px", border: "solid #ddd 1px" }}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="max-h-[300px] flex items-center justify-center">
        {loading ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Loading chart...
          </div>
        ) : categoryData.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            No data available
          </div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </div>
    </div>
  );
};

export default HighLevelPieChart;